import { AI_SUITE_LAUNCH_VIDEO } from '@content/press/releases';

export type FeatureIconKey =
  | 'user-group'
  | 'inbox-stack'
  | 'clipboard'
  | 'banknotes'
  | 'document-text'
  | 'credit-card'
  | 'shield-check'
  | 'chart-bar'
  | 'calculator'
  | 'server'
  | 'sparkles';

export type PillIconKey = 'play-circle' | 'sparkles';

export type FeaturePill = {
  label: string;
  icon: PillIconKey;
};

type FeatureBase = {
  key: string;
  title: string; // required for analytics (feature_title)
  description: string;
  icon: FeatureIconKey;
  href?: string;
  caption?: string;
  posterSecond?: number;
  colSpan?: 1 | 2;
  isDefault?: boolean;
  isHomeSpotlight?: boolean;
  pills?: FeaturePill[];
};

export type FeatureWithImages = FeatureBase & {
  image?: string;
  imageDark?: string;
  videoSrc?: never;
};

export type FeatureWithVideo = FeatureBase & {
  muxPlaybackId: string;
  image?: never;
  imageDark?: never;
};

export type Feature = FeatureWithImages | FeatureWithVideo;

export const FEATURES: Feature[] = [
  {
    key: 'ai-suite',
    title: 'Integrated AI',
    description: 'AI for enquiries, paperwork and confirmations, plus answers about your business.',
    icon: 'sparkles',
    href: '/features/integrated-ai',
    caption: 'Watch Bunker Suite AI in action, from incoming enquiries to delivery paperwork.',
    posterSecond: 12,
    colSpan: 2,
    isHomeSpotlight: true,
    pills: [
      { label: 'Video', icon: 'play-circle' },
      { label: 'New', icon: 'sparkles' },
    ],
    muxPlaybackId: AI_SUITE_LAUNCH_VIDEO,
  },
  {
    key: 'org',
    title: 'Organisation & Accounts',
    description: 'Central users, roles, and permissions.',
    icon: 'user-group',
  },
  {
    key: 'crm',
    title: 'CRM',
    description: 'Accounts, contacts, reminders, and activity timelines built for bunkering.',
    icon: 'inbox-stack',
  },
  {
    key: 'requests',
    title: 'Requests (RFQs)',
    description: 'Create RFQs, compare quotes, and track results end-to-end.',
    icon: 'clipboard',
  },
  {
    key: 'transactions',
    title: 'Transactions',
    description: 'Confirmations, recon, and audits with real-time status controls.',
    icon: 'banknotes',
  },
  {
    key: 'edocs',
    title: 'Email & Documents',
    description: 'Generate, store, and send documents with secure workflows.',
    icon: 'document-text',
    href: '/features/email-documents',
    caption:
      'Watch how bunker teams generate confirmations, store BDNs, and keep supporting documents connected in one workflow.',
    posterSecond: 20,
    pills: [{ label: 'Video', icon: 'play-circle' }],
    // Keep this video configured separately from E-BDN so the homepage spotlight can switch back to Email & Documents.
    muxPlaybackId: '02wcqTCj200300kjHBXdpGIsVk00ryw9jzB4BgQzlYhfewA',
  },
  {
    key: 'credit',
    title: 'Credit Management',
    description: 'Limits, exposure, holds/releases, and approval routing.',
    icon: 'credit-card',
  },
  {
    key: 'compliance',
    title: 'Risk & Compliance',
    description: 'Counterparty screening, policy checks, and evidence.',
    icon: 'shield-check',
  },
  {
    key: 'analytics',
    title: 'Analytics & Data',
    description: 'Live dashboards for volumes, margins, positions, and risk.',
    icon: 'chart-bar',
  },
  {
    key: 'finance',
    title: 'Finance',
    description: 'Invoicing, AP/AR, settlements; export to ERP/accounting.',
    icon: 'calculator',
  },
  {
    key: 'integrations',
    title: 'Integrations',
    description: 'APIs for ERPs, accounting, and data providers.',
    icon: 'server',
  },
  {
    key: 'electronical-docs',
    title: 'Electronic Documents (E-BDN)',
    description: 'Create, exchange, and sign E-BDNs (and other documents) with full traceability.',
    icon: 'document-text',
    caption: 'Watch the E-BDN workflow in action on board the Lady Clara and Till Benelux.',
    posterSecond: 30,
    href: '/features/electronic-documents',
    colSpan: 2,
    isDefault: true,
    // Keep the existing E-BDN video in code so the homepage spotlight can be switched back easily later.
    pills: [
      { label: 'Video', icon: 'play-circle' },
      { label: 'New', icon: 'sparkles' },
    ],
    muxPlaybackId: 'EGjWGAP02fYKPF02qzxHFRdtRLfkW02fTi8bxLXksBklTg',
  },
];

export const DEFAULT_FEATURE = FEATURES.find((f) => f.isDefault) ?? FEATURES[0] ?? null;
export const DEFAULT_FEATURE_TITLE = DEFAULT_FEATURE?.title ?? '';

// Dev-time validation to ensure analytics contracts
if (process.env.NODE_ENV !== 'production') {
  for (const f of FEATURES) {
    if (!f.key || !f.title) {
      console.error('Invalid feature entry, missing key/title:', f);
      throw new Error('FEATURES validation failed: each feature requires key and title');
    }
  }
}
